import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  profileApi,
  type WalletAccountLogItem,
  type WalletSummaryResult,
  type WithdrawConfigResult,
  type WithdrawLogItem,
} from "@/api";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";
import {
  invokeWechatJsapiPay,
  isWechatJsapiPayConfig,
  isWechatPayAlreadyPaid,
  waitForWechatPayResultReady,
} from "@/lib/wechat-pay";
import { invokeWechatMerchantTransfer } from "@/lib/wechat-transfer";

interface Transaction {
  id: number;
  type: "income" | "expense" | "refund";
  title: string;
  desc: string;
  amount: string;
  date: string;
  time: string;
}

interface WithdrawOption {
  id: number;
  name: string;
  tail: string;
  icon: string;
  color: string;
}

const DEFAULT_CARD_AMOUNTS = ["100", "300", "500", "1000"];

const RECHARGE_PAY_WAY = 1;

function toNumber(value?: number | string | null) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function getSignedLogAmount(log: WalletAccountLogItem) {
  const rawAmount = toNumber(log.change_amount);
  const action = Number(log.action);
  if (rawAmount !== 0) {
    return rawAmount;
  }
  if (action === 1) {
    return -Math.abs(rawAmount);
  }
  return Math.abs(rawAmount);
}

function formatMoney(value?: number | string | null) {
  return `¥${toNumber(value).toFixed(2)}`;
}

function formatMoneyInteger(value?: number | string | null) {
  return `¥${toNumber(value).toFixed(0)}`;
}

function normalizeCardAmount(value?: number | string | null) {
  const amount = toNumber(value);
  if (amount <= 0) {
    return "";
  }
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

function getCardAmounts(summary?: WalletSummaryResult | null) {
  const configured = Array.isArray(summary?.card_amounts) ? summary.card_amounts : [];
  const amounts = configured.map(normalizeCardAmount).filter(Boolean);
  return amounts.length > 0 ? amounts : DEFAULT_CARD_AMOUNTS;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function mapLogType(log: WalletAccountLogItem): Transaction["type"] {
  const title = String(log.change_type_desc || "");
  const signedAmount = getSignedLogAmount(log);
  const action = Number(log.action);
  if (signedAmount < 0 || action === 1) {
    return "expense";
  }
  if ((signedAmount > 0 || action === 2) && title.includes("退款")) {
    return "refund";
  }
  if (action === 2 || title.includes("充值") || title.includes("购卡") || title.includes("收入")) {
    return "income";
  }
  return "expense";
}

function formatDateParts(raw?: string) {
  if (!raw) {
    return { date: "--", time: "--:--" };
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    const [dateText = "--", timeText = "--:--"] = raw.replace("T", " ").split(" ");
    return {
      date: dateText.slice(5),
      time: timeText.slice(0, 5) || "--:--",
    };
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return {
    date: `${month}-${day}`,
    time: `${hour}:${minute}`,
  };
}

function normalizeTransaction(log: WalletAccountLogItem): Transaction {
  const type = mapLogType(log);
  const amount = Math.abs(getSignedLogAmount(log));
  const { date, time } = formatDateParts(log.create_time);
  const title = String(log.change_type_desc || "账户变动").replace("余额充值", "购卡到账");
  const sign = type === "expense" ? "-" : "+";
  return {
    id: Number(log.id || Date.now()),
    type,
    title,
    desc: String(log.remark || "钱包资金变动"),
    amount: `${sign}${amount.toFixed(2)}`,
    date,
    time,
  };
}

function buildWithdrawOptions(config?: WithdrawConfigResult | null): WithdrawOption[] {
  const types = Array.isArray(config?.type) ? config.type : [];
  return types.map((item) => {
    const value = Number(item.value || 0);
    if (value === 2) {
      return {
        id: value,
        name: String(item.name || "微信零钱"),
        tail: "微信零钱",
        icon: "ri-wechat-pay-line",
        color: "#17B978",
      };
    }
    if (value === 3) {
      return {
        id: value,
        name: String(item.name || "银行卡"),
        tail: "银行卡",
        icon: "ri-bank-card-line",
        color: "#E63434",
      };
    }
    if (value === 5) {
      return {
        id: value,
        name: String(item.name || "支付宝收款码"),
        tail: "支付宝收款码",
        icon: "ri-alipay-line",
        color: "#1677FF",
      };
    }
    if (value === 4) {
      return {
        id: value,
        name: String(item.name || "微信收款码"),
        tail: "微信收款码",
        icon: "ri-qr-code-line",
        color: "#17B978",
      };
    }
    return {
      id: value || 1,
      name: String(item.name || "钱包余额"),
      tail: "钱包余额",
      icon: "ri-wallet-3-line",
      color: "#FF7B22",
    };
  });
}

export default function WalletPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [summary, setSummary] = useState<WalletSummaryResult | null>(null);
  const [logs, setLogs] = useState<WalletAccountLogItem[]>([]);
  const [withdrawConfig, setWithdrawConfig] = useState<WithdrawConfigResult | null>(null);
  const [pageError, setPageError] = useState("");

  const [activeTab, setActiveTab] = useState<"all" | "income" | "expense">("all");
  const [showRecharge, setShowRecharge] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(DEFAULT_CARD_AMOUNTS[0]);
  const [rechargeSuccess, setRechargeSuccess] = useState(false);
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [rechargeError, setRechargeError] = useState("");

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [selectedBank, setSelectedBank] = useState<number | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawRealName, setWithdrawRealName] = useState("");
  const [withdrawLogs, setWithdrawLogs] = useState<WithdrawLogItem[]>([]);
  const [confirmingWithdrawId, setConfirmingWithdrawId] = useState<number | null>(null);

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  const loadWalletData = async () => {
    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    try {
      const [walletResult, logResult, withdrawResult, withdrawLogResult] = await Promise.all([
        profileApi.getWallet(),
        profileApi.getWalletAccountLogs({ page_no: 1, page_size: 30 }),
        profileApi.getWithdrawConfig(),
        profileApi.getWithdrawLogs({ page_no: 1, page_size: 10 }),
      ]);
      setSummary(walletResult);
      setLogs(Array.isArray(logResult.lists) ? logResult.lists : []);
      setWithdrawConfig(withdrawResult);
      setWithdrawLogs(Array.isArray(withdrawLogResult.lists) ? withdrawLogResult.lists : []);

      const options = buildWithdrawOptions(withdrawResult);
      setSelectedBank((current) => current ?? options[0]?.id ?? null);
      setPageError("");
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setPageError(getErrorMessage(error, "钱包数据加载失败，请稍后重试"));
    }
  };

  useEffect(() => {
    void loadWalletData();
  }, []);

  const cardAmounts = useMemo(() => getCardAmounts(summary), [summary]);

  useEffect(() => {
    if (!cardAmounts.includes(selectedAmount)) {
      setSelectedAmount(cardAmounts[0] || "");
    }
  }, [cardAmounts, selectedAmount]);

  const transactions = useMemo(() => logs.map(normalizeTransaction), [logs]);

  const filtered = useMemo(() => {
    return transactions.filter((item) => {
      if (activeTab === "income") {
        return item.type === "income" || item.type === "refund";
      }
      if (activeTab === "expense") {
        return item.type === "expense";
      }
      return true;
    });
  }, [activeTab, transactions]);

  const grouped = useMemo(() => {
    return filtered.reduce<Record<string, Transaction[]>>((groups, item) => {
      if (!groups[item.date]) {
        groups[item.date] = [];
      }
      groups[item.date].push(item);
      return groups;
    }, {});
  }, [filtered]);

  const stats = useMemo(() => {
    return transactions.reduce(
      (result, item) => {
        const amount = Math.abs(Number(item.amount));
        if (item.type === "income") {
          result.recharge += amount;
        } else if (item.type === "refund") {
          result.refund += amount;
        } else {
          result.expense += amount;
        }
        return result;
      },
      { recharge: 0, expense: 0, refund: 0 },
    );
  }, [transactions]);

  const finalAmount = selectedAmount;
  const purchaseOpen = Number(summary?.recharge_open ?? 1) === 1;
  const withdrawOptions = useMemo(() => buildWithdrawOptions(withdrawConfig), [withdrawConfig]);
  const availableWithdrawAmount = toNumber(withdrawConfig?.user_earnings ?? summary?.user_earnings);

  const handleRecharge = async () => {
    if (!finalAmount || Number(finalAmount) <= 0) {
      return;
    }
    if (!purchaseOpen) {
      setRechargeError("钱包购卡暂未开放");
      return;
    }
    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    setRechargeSubmitting(true);
    setRechargeError("");

    try {
      const rechargeResult = await profileApi.applyRecharge({
        money: Number(finalAmount),
        pay_way: RECHARGE_PAY_WAY,
      });
      const prepayResult = await profileApi.prepayFinance({
        from: "recharge",
        order_id: rechargeResult.order_id,
        pay_way: RECHARGE_PAY_WAY,
      });
      if (isWechatPayAlreadyPaid(prepayResult.config)) {
        await waitForWechatPayResultReady(() => profileApi.getFinancePayResult(rechargeResult.order_id, "recharge"));
      } else if (isWechatJsapiPayConfig(prepayResult.config)) {
        await invokeWechatJsapiPay(prepayResult.config);
        await waitForWechatPayResultReady(() => profileApi.getFinancePayResult(rechargeResult.order_id, "recharge"));
      } else {
      const notifyResult = await profileApi.mockFinancePayNotify(
        rechargeResult.order_id,
        "success",
        RECHARGE_PAY_WAY,
        "recharge",
      );
      if (Number(notifyResult.pay_status) !== 1) {
        throw new Error(notifyResult.message || "购卡失败");
      }
      }
      const payResult = await profileApi.getFinancePayResult(rechargeResult.order_id, "recharge");
      if (Number(payResult.pay_status) !== 1) {
        throw new Error("购卡失败");
      }

      await loadWalletData();
      setRechargeSuccess(true);
      window.setTimeout(() => {
        setRechargeSuccess(false);
        setShowRecharge(false);
      }, 1800);
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setRechargeError(getErrorMessage(error, "购卡失败，请稍后重试"));
    } finally {
      setRechargeSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawError("用户钱包余额暂不支持提现，请通过订单退款流程处理");
    return;
    if (!withdrawAmount || Number(withdrawAmount) <= 0 || !selectedBank) {
      return;
    }
    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }
    if (Number(withdrawAmount) > availableWithdrawAmount) {
      setWithdrawError("提现金额不能超过可提现收益");
      return;
    }
    if ((selectedBank === 2 || selectedBank === 4) && Number(withdrawAmount) >= 2000 && !withdrawRealName.trim()) {
      setWithdrawError("请输入真实姓名");
      return;
    }

    setWithdrawSubmitting(true);
    setWithdrawError("");

    try {
      await profileApi.applyWithdraw({
        type: selectedBank,
        money: Number(withdrawAmount),
        real_name: withdrawRealName.trim(),
      });
      await loadWalletData();
      setWithdrawSuccess(true);
      window.setTimeout(() => {
        setWithdrawSuccess(false);
        setShowWithdraw(false);
        setWithdrawAmount("");
        setWithdrawRealName("");
      }, 1800);
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setWithdrawError(getErrorMessage(error, "提现申请失败，请稍后重试"));
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const pendingWechatConfirm = withdrawLogs.find((item) => false && item.wechat_confirm_required);

  const handleConfirmWechatWithdraw = async (id: number) => {
    setConfirmingWithdrawId(id);
    try {
      const result = await profileApi.prepareWechatWithdraw(id);
      if (result.need_user_confirm && result.package_info) {
        await invokeWechatMerchantTransfer(result);
      }
      await loadWalletData();
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setPageError(getErrorMessage(error, "微信确认收款失败，请稍后重试"));
    } finally {
      setConfirmingWithdrawId(null);
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen pb-8"
      style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="relative overflow-hidden px-5 pt-12 pb-6"
        style={{ background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="absolute bottom-0 left-0 w-28 h-28 rounded-full -translate-x-8 translate-y-8" style={{ background: "rgba(255,255,255,0.04)" }} />

        <div className="flex items-center gap-3 relative z-10 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-arrow-left-s-line text-lg text-white" />
          </button>
          <h1 className="text-base font-bold text-white flex-1">我的钱包</h1>
          <button
            onClick={() => navigate("/service")}
            className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.15)" }}
          >
            <i className="ri-customer-service-2-line text-sm text-white" />
          </button>
        </div>

        <div
          className="relative overflow-hidden rounded-2xl p-5"
          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] mb-1" style={{ color: "rgba(255,255,255,0.65)" }}>账户余额</p>
              <div className="flex items-end gap-1">
                <span className="text-sm font-semibold text-white">¥</span>
                <span className="text-4xl font-black text-white leading-none">
                  {toNumber(summary?.user_money).toFixed(2).split(".")[0]}
                </span>
                <span className="text-sm font-semibold text-white mb-0.5">
                  .{toNumber(summary?.user_money).toFixed(2).split(".")[1]}
                </span>
              </div>
              <p className="text-[11px] mt-2" style={{ color: "rgba(255,255,255,0.5)" }}>
                本月消费 {formatMoney(stats.expense)} · 累计购卡 {formatMoney(stats.recharge)}
              </p>
            </div>
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <i className="ri-wallet-3-line text-xl text-white" />
            </div>
          </div>

          {purchaseOpen ? (
            <div className="mt-5">
              <button
                onClick={() => {
                  setRechargeError("");
                  setShowRecharge(true);
                }}
                className="w-full py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap text-white transition-all"
                style={{ background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)" }}
              >
                <i className="ri-bank-card-line mr-1.5" />
                购卡
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {pageError ? (
        <div className="px-5 pt-4">
          <div
            className="rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {pageError}
          </div>
        </div>
      ) : null}

      {pendingWechatConfirm ? (
        <div className="px-5 pt-4">
          <div
            className="rounded-2xl px-4 py-3 flex items-center gap-3"
            style={{ background: "#F0FBF5", border: "1px solid #BDECD4" }}
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#DDF7EA" }}>
              <i className="ri-wechat-pay-line text-lg" style={{ color: "#17A87B" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>待确认收款</p>
              <p className="text-[11px] mt-0.5" style={{ color: "#6C8B79" }}>
                {pendingWechatConfirm.sn || "微信提现"} {formatMoney(pendingWechatConfirm.money)}
              </p>
            </div>
            <button
              onClick={() => void handleConfirmWechatWithdraw(pendingWechatConfirm.id)}
              disabled={confirmingWithdrawId === pendingWechatConfirm.id}
              className="px-3 py-2 rounded-xl text-xs font-bold text-white cursor-pointer whitespace-nowrap disabled:opacity-70"
              style={{ background: "#17A87B" }}
            >
              {confirmingWithdrawId === pendingWechatConfirm.id ? "确认中" : "去确认"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="px-5 pt-4">
        <div
          className="grid grid-cols-3 rounded-2xl overflow-hidden"
          style={{ background: "#fff", border: "1px solid #F0EAE0" }}
        >
          {[
            { label: "累计购卡", value: formatMoneyInteger(stats.recharge), icon: "ri-bank-card-line", color: "#17A87B", bg: "#EDFBF4" },
            { label: "累计消费", value: formatMoneyInteger(stats.expense), icon: "ri-shopping-bag-line", color: "#FF6018", bg: "#FFF4EC" },
            { label: "累计退款", value: formatMoneyInteger(stats.refund), icon: "ri-refund-line", color: "#E8860A", bg: "#FFF8E7" },
          ].map((item, index) => (
            <div
              key={item.label}
              className={`flex flex-col items-center py-4 ${index < 2 ? "border-r" : ""}`}
              style={{ borderColor: "#F5F2EE" }}
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-2" style={{ background: item.bg }}>
                <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
              </div>
              <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{item.value}</p>
              <p className="text-[10px] mt-0.5" style={{ color: "#BBAAA0" }}>{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 pt-5 flex-1">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: "#EEEBE6" }}>
            {([
              { key: "all", label: "全部" },
              { key: "income", label: "收入" },
              { key: "expense", label: "支出" },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                style={{
                  background: activeTab === tab.key ? "#fff" : "transparent",
                  color: activeTab === tab.key ? "#FF6018" : "#888",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <span className="text-[11px]" style={{ color: "#BBAAA0" }}>{filtered.length} 条记录</span>
        </div>

        <div className="space-y-4">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <p className="text-[11px] font-semibold mb-2 px-0.5" style={{ color: "#BBAAA0" }}>
                {date}
              </p>
              <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid #F0EAE0" }}>
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 px-4 py-3.5 ${index < items.length - 1 ? "border-b" : ""}`}
                    style={{ borderColor: "#F8F5F1" }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background:
                          item.type === "income" ? "#EDFBF4"
                          : item.type === "refund" ? "#FFF8E7"
                          : "#FFF4EC",
                      }}
                    >
                      <i
                        className={
                          item.type === "income" ? "ri-wallet-line text-base"
                          : item.type === "refund" ? "ri-refund-line text-base"
                          : "ri-service-line text-base"
                        }
                        style={{
                          color:
                            item.type === "income" ? "#17A87B"
                            : item.type === "refund" ? "#E8860A"
                            : "#FF6018",
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: "#1A1A1A" }}>{item.title}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
                        {item.desc} · {item.time}
                      </p>
                    </div>
                    <p
                      className="text-sm font-bold flex-shrink-0"
                      style={{
                        color:
                          item.type === "income" ? "#17A87B"
                          : item.type === "refund" ? "#E8860A"
                          : "#1A1A1A",
                      }}
                    >
                      {item.amount}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "#FFF4EC" }}>
              <i className="ri-wallet-3-line text-3xl" style={{ color: "#FFB07A" }} />
            </div>
            <p className="text-sm" style={{ color: "#BBAAA0" }}>暂无交易记录</p>
          </div>
        ) : null}
      </div>

      {showRecharge ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !rechargeSuccess && !rechargeSubmitting && setShowRecharge(false)} />
          <div className="relative w-full max-w-[480px] bg-white rounded-t-3xl pb-10 z-10">
            <div className="w-10 h-1 rounded-full mx-auto mt-3 mb-5" style={{ background: "#E0D8D0" }} />

            {rechargeSuccess ? (
              <div className="flex flex-col items-center py-10 px-5">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                  style={{ background: "linear-gradient(135deg, #EDFBF4, #D0F5E8)" }}
                >
                  <i className="ri-check-double-line text-4xl" style={{ color: "#17A87B" }} />
                </div>
                <p className="text-lg font-black mb-1" style={{ color: "#1A1A1A" }}>购卡成功！</p>
                <p className="text-sm mb-1" style={{ color: "#BBAAA0" }}>
                  <span className="font-bold" style={{ color: "#FF6018" }}>¥{finalAmount}</span> 已到账我的钱包
                </p>
              </div>
            ) : (
              <div className="px-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold" style={{ color: "#1A1A1A" }}>购买钱包卡</h3>
                    <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>
                      当前余额
                      <span className="font-bold ml-1" style={{ color: "#FF6018" }}>{formatMoney(summary?.user_money)}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => !rechargeSubmitting && setShowRecharge(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
                    style={{ background: "#F5F2EE" }}
                  >
                    <i className="ri-close-line text-base" style={{ color: "#999" }} />
                  </button>
                </div>

                <p className="text-xs font-semibold mb-2.5" style={{ color: "#888" }}>选择购卡金额</p>
                <div className="grid grid-cols-2 gap-2.5 mb-5">
                  {cardAmounts.map((amount) => {
                    const isActive = selectedAmount === amount;
                    return (
                      <button
                        key={amount}
                        onClick={() => setSelectedAmount(amount)}
                        className="relative flex flex-col items-center py-4 rounded-2xl cursor-pointer transition-all"
                        style={{
                          background: isActive ? "linear-gradient(135deg, #FFF4EC, #FFE8D5)" : "#F7F5F2",
                          border: `2px solid ${isActive ? "#FF7A30" : "transparent"}`,
                        }}
                      >
                        <span
                          className="text-xl font-black leading-none"
                          style={{ color: isActive ? "#FF5000" : "#333" }}
                        >
                          ¥{amount}
                        </span>
                        {isActive ? (
                          <div
                            className="absolute bottom-2 right-2 w-4 h-4 rounded-full flex items-center justify-center"
                            style={{ background: "#FF6018" }}
                          >
                            <i className="ri-check-line text-[9px] text-white" />
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs font-semibold mb-2.5" style={{ color: "#888" }}>支付方式</p>
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl mb-3"
                  style={{ background: "#F2FBF5", border: "1.5px solid #17B978" }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-pay-line text-2xl" style={{ color: "#17B978" }} />
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap" style={{ color: "#1A1A1A" }}>微信支付</span>
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                    style={{ background: "#17B97818", color: "#17B978" }}
                  >
                    推荐
                  </span>
                  <div className="flex-1" />
                  <div
                    className="flex-shrink-0 rounded-full flex items-center justify-center"
                    style={{ width: 18, height: 18, background: "#17B978" }}
                  >
                    <i className="ri-check-line text-[10px] text-white" />
                  </div>
                </div>

                {rechargeError ? (
                  <p className="text-[11px] mb-4" style={{ color: "#D94A00" }}>{rechargeError}</p>
                ) : null}

                <button
                  onClick={() => void handleRecharge()}
                  disabled={rechargeSubmitting || !finalAmount || Number(finalAmount) <= 0}
                  className="w-full py-4 rounded-2xl font-black cursor-pointer whitespace-nowrap text-white"
                  style={{
                    fontSize: 15,
                    background: finalAmount && Number(finalAmount) > 0
                      ? "linear-gradient(135deg, #FF7A30 0%, #FF4D00 100%)"
                      : "#E8E0D8",
                    color: finalAmount && Number(finalAmount) > 0 ? "#fff" : "#B0A898",
                  }}
                >
                  {rechargeSubmitting
                    ? "购卡中..."
                    : finalAmount && Number(finalAmount) > 0
                      ? `确认购卡 ¥${finalAmount}`
                      : "请选择购卡金额"}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showWithdraw ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !withdrawSuccess && !withdrawSubmitting && setShowWithdraw(false)} />
          <div className="relative w-full max-w-[480px] bg-white rounded-t-3xl pb-10 z-10">
            <div className="w-10 h-1 rounded-full mx-auto mt-3 mb-5" style={{ background: "#E0D8D0" }} />

            {withdrawSuccess ? (
              <div className="flex flex-col items-center py-8 px-5">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: "#EDFBF4" }}>
                  <i className="ri-check-double-line text-3xl" style={{ color: "#17A87B" }} />
                </div>
                <p className="text-base font-bold mb-1" style={{ color: "#1A1A1A" }}>已提交审核</p>
              </div>
            ) : (
              <div className="px-5">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-base font-bold" style={{ color: "#1A1A1A" }}>余额提现</h3>
                  <button onClick={() => !withdrawSubmitting && setShowWithdraw(false)} className="cursor-pointer">
                    <i className="ri-close-line text-xl" style={{ color: "#999" }} />
                  </button>
                </div>

                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl mb-5"
                  style={{ background: "#FFF8F3", border: "1px solid #FFE4CC" }}
                >
                  <span className="text-xs" style={{ color: "#CC7A30" }}>可提现余额</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: "#FF6018" }}>{formatMoney(availableWithdrawAmount)}</span>
                    <button
                      onClick={() => setWithdrawAmount(String(availableWithdrawAmount))}
                      className="text-[10px] px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap"
                      style={{ background: "#FF6018", color: "#fff" }}
                    >
                      全部提现
                    </button>
                  </div>
                </div>

                <p className="text-xs font-semibold mb-2" style={{ color: "#888" }}>提现金额</p>
                <div
                  className="flex items-center gap-2 px-4 py-3.5 rounded-xl mb-5"
                  style={{ background: "#F8F6F3", border: `1.5px solid ${withdrawAmount ? "#FF9A3C" : "#EDE8E0"}` }}
                >
                  <span className="text-lg font-bold" style={{ color: "#FF6018" }}>¥</span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(event) => setWithdrawAmount(event.target.value)}
                    placeholder="请输入提现金额"
                    className="flex-1 text-base font-bold outline-none bg-transparent"
                    style={{ color: "#1A1A1A" }}
                  />
                  {withdrawAmount ? (
                    <button onClick={() => setWithdrawAmount("")} className="cursor-pointer flex-shrink-0">
                      <i className="ri-close-circle-fill text-base" style={{ color: "#CCCCCC" }} />
                    </button>
                  ) : null}
                </div>

                {(selectedBank === 2 || selectedBank === 4) && Number(withdrawAmount) >= 2000 ? (
                  <>
                    <p className="text-xs font-semibold mb-2" style={{ color: "#888" }}>真实姓名</p>
                    <div
                      className="flex items-center gap-2 px-4 py-3.5 rounded-xl mb-5"
                      style={{ background: "#F8F6F3", border: `1.5px solid ${withdrawRealName ? "#FF9A3C" : "#EDE8E0"}` }}
                    >
                      <input
                        value={withdrawRealName}
                        onChange={(event) => setWithdrawRealName(event.target.value)}
                        placeholder="请输入收款人真实姓名"
                        className="flex-1 text-sm font-semibold outline-none bg-transparent"
                        style={{ color: "#1A1A1A" }}
                      />
                    </div>
                  </>
                ) : null}

                <p className="text-xs font-semibold mb-2" style={{ color: "#888" }}>提现至</p>
                <div className="space-y-2 mb-2">
                  {withdrawOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setSelectedBank(option.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer text-left"
                      style={{
                        background: selectedBank === option.id ? "#FFF8F3" : "#F8F6F3",
                        border: `1.5px solid ${selectedBank === option.id ? "#FF9A3C" : "#EDE8E0"}`,
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: "#fff" }}
                      >
                        <i className={`${option.icon} text-lg`} style={{ color: option.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>{option.name}</p>
                        <p className="text-[11px] mt-0.5" style={{ color: "#BBAAA0" }}>{option.tail}</p>
                      </div>
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          background: selectedBank === option.id ? "#FF6018" : "transparent",
                          border: `1.5px solid ${selectedBank === option.id ? "#FF6018" : "#D0C8BE"}`,
                        }}
                      >
                        {selectedBank === option.id ? <i className="ri-check-line text-xs text-white" /> : null}
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] mb-2 px-1" style={{ color: "#BBAAA0" }}>
                  提现预计 1-3 个工作日到账，手续费全免
                </p>
                {withdrawError ? (
                  <p className="text-[11px] mb-4 px-1" style={{ color: "#D94A00" }}>{withdrawError}</p>
                ) : null}

                <button
                  onClick={() => void handleWithdraw()}
                  disabled={withdrawSubmitting || !withdrawAmount || Number(withdrawAmount) <= 0}
                  className="w-full py-4 rounded-2xl text-sm font-bold cursor-pointer whitespace-nowrap"
                  style={{
                    background: withdrawAmount && Number(withdrawAmount) > 0
                      ? "linear-gradient(90deg, #FF5C00, #FF9A3C)"
                      : "#E8E0D8",
                    color: withdrawAmount && Number(withdrawAmount) > 0 ? "#fff" : "#B0A898",
                  }}
                >
                  {withdrawSubmitting ? "提交中..." : `确认提现 ${withdrawAmount ? `¥${withdrawAmount}` : ""}`}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
